export const INTL_INVENTORY_ID = 'intl_inventory'
export const ESW_ORDERABLE_REFINEMENT = 'c_eswIsOrderableSku=true'

/**
 * Resolves a variant's orderability for the active locale.
 *
 * `c_eswIsOrderableSku` is a persisted product attribute derived from the INTL
 * inventory list, so it is present on the Shopper Products response for every
 * locale (including non-ESW UK/default). It must only override the native
 * `orderable` flag for ESW-supported (international) locales; otherwise UK/default
 * shoppers would see availability driven by INTL inventory instead of site stock.
 *
 * @param {Object} variant - Variant (or variant-like) document.
 * @param {boolean} isInternational - True when the current locale is ESW-supported.
 * @returns {boolean|undefined} The orderability to use for availability decisions.
 */
export const getVariantOrderableForLocale = (variant, isInternational) =>
    isInternational && typeof variant?.c_eswIsOrderableSku === 'boolean'
        ? variant.c_eswIsOrderableSku
        : variant?.orderable

/**
 * Returns the locale-specific parameters required by Shopper Products requests.
 * Passing these explicitly prevents client-side product queries from falling back
 * to the site's default UK locale, currency, and inventory.
 *
 * @param {Object} locale - Current multisite locale descriptor.
 * @returns {Object} Shopper Products query parameters.
 */
export const getProductRequestParametersForLocale = (locale) => {
    if (!locale) {
        return {}
    }

    return {
        ...(locale.id ? {locale: locale.id} : {}),
        ...(locale.preferredCurrency ? {currency: locale.preferredCurrency} : {}),
        ...(locale.isSupportedByESW ? {inventoryIds: INTL_INVENTORY_ID} : {})
    }
}

/**
 * Resolves the inventory that should drive storefront availability.
 * Shopper Products keeps the site inventory in `inventory` even when an
 * additional inventory is requested through `inventoryIds`.
 *
 * @param {Object} product - Shopper Products response.
 * @param {Object} locale - Current multisite locale descriptor.
 * @returns {Object|undefined} Locale-specific inventory.
 */
export const getProductInventoryForLocale = (product, locale) => {
    if (!product) {
        return undefined
    }

    if (!locale?.isSupportedByESW) {
        return product.inventory
    }

    return (
        product.inventories?.find(({id}) => id === INTL_INVENTORY_ID) ||
        (product.inventory?.id === INTL_INVENTORY_ID ? product.inventory : undefined)
    )
}

/**
 * Returns basket item inventory fields for ESW-supported INTL locales only.
 * UK/default locales receive an empty object so BM/site inventory behavior remains unchanged.
 *
 * @param {Object} locale - Current multisite locale descriptor.
 * @returns {Object} Basket item fields to merge into add/update payloads.
 */
export const getBasketItemInventoryFieldsForLocale = (locale) => {
    const inventoryId = locale?.isSupportedByESW ? INTL_INVENTORY_ID : undefined
    return inventoryId ? {inventoryId} : {}
}
